/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { type FunctionComponent, type JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouteError } from "react-router-dom";

// The component, not the barrel: the barrel also pulls in the Redux-connected
// container, and this boundary must not depend on the store being alive.
import AlertOverlay from "../common/AlertOverlay/AlertOverlay";
import {
  APP_LOAD_ERROR_EXIT_ACTION_ID,
  APP_LOAD_ERROR_RELOAD_ACTION_ID,
  AppLoadErrorRecovery,
  buildAppLoadErrorActions,
  buildSosErrorActions,
  exitAppFromLoadError,
  getAppLoadErrorMessage,
  getAppLoadErrorTitle,
  getSosErrorMessage,
  getSosErrorTitle,
  logAppLoadError,
  planAppLoadErrorRecovery,
  reloadAppFromLoadError,
  runSilentAppRecovery
} from "../../viewModel/appRecovery";
import type { TAppLoadErrorRecovery } from "../../viewModel/appRecovery";
import type { AlertOverlayAction } from "../../viewModel/alertOverlay";

/**
 * `event.keyCode` values for the keys handled below. Deprecated in the spec but
 * the only identifier some TV remotes send, which is why the platform key
 * mappers in `src/model/navigation` read it too. The three values are identical
 * across every platform mapper (browser, tizen, webos, vegaos).
 */
const KEY_CODE_ENTER: number = 13;
const KEY_CODE_LEFT: number = 37;
const KEY_CODE_RIGHT: number = 39;

/**
 * Root `errorElement` for the route tree: the app's answer to a screen that
 * could not be loaded or rendered at all.
 *
 * Every screen is behind a route-level `import()`, so a chunk that fails to
 * download — a redeploy that replaced the hashed filenames while the app was
 * running, a Wi-Fi drop mid-fetch, a CDN blip — used to leave react-router's
 * own developer error page on screen ("Unexpected Application Error!
 * ChunkLoadError"), which is not something a TV user can act on.
 *
 * Two-step recovery:
 *
 *   1. The first chunk failure — and the first hit on a URL that matches no
 *      route — **silently** restarts the app at `/`: nothing is painted, the
 *      fresh `index.html` references chunks that exist, and `/` always
 *      resolves. Each attempt is recorded so neither can loop.
 *   2. If the same failure comes back, the error overlay is shown with Reload
 *      and, on TV / VegaOS, Exit. If the session has already spent its whole
 *      silent-restart budget (`restartLimitGuard`, shared across every silent
 *      recovery this boundary attempts), the SOS overlay is shown instead —
 *      Exit only, no Reload, since another restart is not on the table.
 *
 * This boundary replaces the whole app tree, so the shared `AlertOverlay`
 * *presentation* is reused but none of its machinery is: the Redux overlay
 * slice and the D-pad engine both live inside the `App` element that just
 * unmounted. Hence the local focus state and the plain `keydown` listener —
 * left/right to move between the buttons and OK to press one, the same mapping
 * every platform's key mapper produces.
 */
const RootErrorBoundary: FunctionComponent = (): JSX.Element | null => {
  const error: unknown = useRouteError();

  // Decided once, before the first paint: a silent recovery must not flash the
  // overlay on its way out. `planAppLoadErrorRecovery` is a read-only check.
  const [recovery, setRecovery] = useState<TAppLoadErrorRecovery>(
    (): TAppLoadErrorRecovery => planAppLoadErrorRecovery(error)
  );
  const isSos: boolean = recovery === AppLoadErrorRecovery.Sos;
  const isRecovering: boolean =
    recovery !== AppLoadErrorRecovery.Overlay && !isSos;
  // Settles once, when `recovery` reaches its terminal value — see `isSos`
  // above — and stays referentially stable afterward, same as the `useState`
  // this replaced: the keydown listener effect below depends on `actions` and
  // must not tear down and re-attach itself on every unrelated re-render (an
  // arrow-key press moving focus, Reload's pending state).
  const actions: AlertOverlayAction[] = useMemo<AlertOverlayAction[]>(
    (): AlertOverlayAction[] =>
      isSos ? buildSosErrorActions() : buildAppLoadErrorActions(),
    [isSos]
  );
  const [focusedActionIndex, setFocusedActionIndex] = useState<number>(0);
  const [isReloadPending, setIsReloadPending] = useState<boolean>(false);
  // Mirrors isReloadPending for the OK-key path, which reads it inside a
  // callback rather than from the render that disabled the button.
  const isReloadPendingRef = useRef<boolean>(false);

  useEffect((): void => {
    logAppLoadError(error, recovery);
    // Logged once for the error that arrived, not on every focus change and
    // not again when a refused recovery falls back to the overlay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect((): void => {
    if (recovery === AppLoadErrorRecovery.Overlay || isSos) {
      return;
    }
    // The recovery can still be refused (a concurrent failure claimed the one
    // allowed attempt, or it could not be persisted). Fall through to the
    // plain overlay rather than sit on a blank screen waiting for a page load
    // — always the plain overlay, deliberately not a fresh SOS re-check: a
    // refusal here almost always means a write failed, in which case
    // re-planning would see the exact same, still-unspent budget and hand
    // back the same silent recovery, leaving this stuck rather than resolved.
    if (!runSilentAppRecovery(error, recovery)) {
      setRecovery(AppLoadErrorRecovery.Overlay);
    }
  }, [recovery, isSos, error]);

  const handleAction = useCallback((actionId: string): void => {
    if (actionId === APP_LOAD_ERROR_EXIT_ACTION_ID) {
      exitAppFromLoadError();
      return;
    }
    if (actionId === APP_LOAD_ERROR_RELOAD_ACTION_ID) {
      if (isReloadPendingRef.current) {
        return;
      }
      // Holds the button in its loading state so a second press cannot land
      // in the gap between here and the page unloading.
      isReloadPendingRef.current = true;
      setIsReloadPending(true);
      reloadAppFromLoadError();
    }
  }, []);

  useEffect((): (() => void) | undefined => {
    if (isRecovering) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      const key: string = event.key;
      const keyCode: number = event.keyCode;

      if (key === "ArrowLeft" || keyCode === KEY_CODE_LEFT) {
        setFocusedActionIndex((index: number): number =>
          index > 0 ? index - 1 : index
        );
        return;
      }
      if (key === "ArrowRight" || keyCode === KEY_CODE_RIGHT) {
        setFocusedActionIndex((index: number): number =>
          index < actions.length - 1 ? index + 1 : index
        );
        return;
      }
      if (key === "Enter" || keyCode === KEY_CODE_ENTER) {
        const action: AlertOverlayAction | undefined =
          actions[focusedActionIndex];
        if (action !== undefined) {
          handleAction(action.id);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return (): void => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isRecovering, actions, focusedActionIndex, handleAction]);

  if (isRecovering) {
    return null;
  }

  return (
    <AlertOverlay
      title={isSos ? getSosErrorTitle() : getAppLoadErrorTitle()}
      message={isSos ? getSosErrorMessage() : getAppLoadErrorMessage()}
      actions={actions}
      focusedActionIndex={focusedActionIndex}
      retryPending={isReloadPending}
      onAction={handleAction}
    />
  );
};

export default RootErrorBoundary;
